import {useEffect, Fragment} from 'react';
import Router from 'next/router';
import {CacheProvider} from '@emotion/react';
import {Provider, useDispatch} from 'react-redux';
import {ThemeProvider} from '@jobseeker/lotus/helpers';
import {gaTrackPageView} from '@jobseeker/common-core/utils/gtag';
import cwvInpLogger from '@jobseeker/common-core/utils/cwvInpLogger';

import {SnackbarProvider} from 'notistack';
import {wrapper} from '@jobseeker/common-core/stores';

import {createEmotionCache} from '@helpers';
import {APP_NAME_IIMJOBS, THEME} from '@constants';
import {metaInfo} from '@configs';

import type {AppProps} from 'next/app';
import type {EmotionCache} from '@emotion/react';
import '../styles/common.css';

import {MaterialDesignContent} from 'notistack';
import {styled} from '@mui/material';
import Head from 'next/head';

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent-success': {
    backgroundColor: isAppIimjobs ? '#49966E' : '#1C2D41',
  },
  '&.notistack-MuiContent-error': {
    backgroundColor: isAppIimjobs ? '#F46C56' : '#EB1C2A',
  },
}));

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

interface WebAppProps extends AppProps {
  emotionCache?: EmotionCache;
}
declare global {
  interface Window {
    __FEATURE_FLAGS__?: Record<string, boolean | string | number>;
  }
}

const FeatureFlagInitializer = () => {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const dispatch = useDispatch<any>();
  /* eslint-enable @typescript-eslint/no-explicit-any */

  useEffect(() => {
    const featureFlags =
      typeof window !== 'undefined' ? window.__FEATURE_FLAGS__ : null;

    if (featureFlags) {
      dispatch({
        type: 'AppContext/set_feature_flags',
        payload: featureFlags,
      });
    }
  }, [dispatch]);

  return null;
};

const WebApp = (props: WebAppProps) => {
  const {Component, emotionCache = clientSideEmotionCache, pageProps} = props;

  const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME || APP_NAME_IIMJOBS;

  const metaData = metaInfo[APP_NAME];

  const theme = metaData[THEME];

  const {store} = wrapper.useWrappedStore(pageProps);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      gaTrackPageView(url, metaData['gaTagId']);
    };
    const handleRouteStart = (url: string) => {
      cwvInpLogger.report(url.split('?')[0]);
    };
    Router.events.on('routeChangeComplete', handleRouteChange);
    if (!isAppIimjobs) Router.events.on('routeChangeStart', handleRouteStart);
    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
      if (!isAppIimjobs)
        Router.events.off('routeChangeStart', handleRouteStart);
    };
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const loader = document.getElementById('globalLoader');
      if (loader) loader.style.display = 'none';
    }
  }, []);

  useEffect(() => {
    if (!isAppIimjobs) cwvInpLogger.start(window.location.pathname);
  }, []);

  return (
    <Fragment>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        ></meta>
      </Head>
      <CacheProvider value={emotionCache}>
        <Provider store={store}>
          <FeatureFlagInitializer />
          <ThemeProvider theme={theme}>
            <Component {...pageProps} />
            <SnackbarProvider
              Components={{
                success: StyledMaterialDesignContent,
                error: StyledMaterialDesignContent,
              }}
              maxSnack={4}
            />
          </ThemeProvider>
        </Provider>
      </CacheProvider>
    </Fragment>
  );
};

export default WebApp;
